import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import './css/AdminDashboardPage.css';
import { 
  GET_QUOTATIONS
} from '../apollo/queries/quotationQueries';
import { 
  GET_ALL_USERS
} from '../apollo/queries/userQueries';

// 간단한 테스트용 쿼리들
const GET_RECENT_SIGNUPS = gql`
  query GetRecentSignups($limit: Int) {
    recentSignups(limit: $limit) {
      id
      email
      name
      phone
      mobile
      company
      department
      createdAt
    }
  }
`;

const Chart = ({ quotations }) => {
  // 현재 연도의 월별 차트 데이터 생성
  const currentYear = new Date().getFullYear();
  
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const monthQuotations = quotations.filter(q => {
      const date = new Date(q.createdAt);
      return date.getFullYear() === currentYear && date.getMonth() + 1 === month;
    }).length;
    return { month, totalQuotations: monthQuotations };
  });
  
  const max = Math.max(...monthlyData.map(item => item.totalQuotations), 1);

  return (
    <div className="chart">
      <div className="chart-y-axis">
        <span>{max}</span>
        <span>{Math.floor(max * 0.66)}</span>
        <span>{Math.floor(max * 0.33)}</span>
      </div>
      <svg className="chart-svg" viewBox="0 0 100 90" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="#e84118"
          strokeWidth="2"
          points={monthlyData.map((item, i) => {
            const value = item.totalQuotations || 0;
            const x = (i / 11) * 100; // 0부터 100까지 균등하게 분배 (퍼센트)
            const y = 85 - (value / max) * 80; // 상단 여백 5px, 하단 여백 5px
            return `${x},${y}`;
          }).join(' ')}
        />
        {/* 데이터 포인트 표시 */}
        {monthlyData.map((item, i) => {
          const value = item.totalQuotations || 0;
          if (value > 0) {
            const x = (i / 11) * 100;
            const y = 85 - (value / max) * 80;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1"
                fill="#e84118"
              />
            );
          }
          return null;
        })}
      </svg>
      <div className="chart-x-axis">
        {monthlyData.map((item, i) => (
          <span key={i}>{item.month}</span>
        ))}
        <span>월</span>
      </div>
    </div>
  );
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  
  // 기본 쿼리들 - 단계별로 테스트
  const { data: quotationsData, loading: quotationsLoading, error: quotationsError } = useQuery(GET_QUOTATIONS);
  const { data: recentSignupsData, loading: signupsLoading, error: signupsError } = useQuery(GET_RECENT_SIGNUPS, {
    variables: { limit: 5 }
  });

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear().toString().slice(-2)}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // 전화번호 포맷팅 함수
  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return phone;
  };

  // 견적 통계 계산
  const calculateQuotationStats = (quotations) => {
    if (!quotations) return { newQuotations: 0, totalQuotations: 0, pendingQuotations: 0, completedQuotations: 0, emailSentQuotations: 0 };

    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    const newQuotations = quotations.filter(q => {
      const createdAt = new Date(q.createdAt);
      return createdAt >= yesterday;
    }).length;

    const pendingQuotations = quotations.filter(q => q.status === 'PENDING').length;
    const completedQuotations = quotations.filter(q => q.status === 'COMPLETED').length;
    const processingQuotations = quotations.filter(q => q.status === 'PROCESSING').length;

    return {
      newQuotations,
      totalQuotations: quotations.length,
      pendingQuotations,
      completedQuotations,
      emailSentQuotations: processingQuotations
    };
  };

  // 데이터 처리
  const quotations = quotationsData?.quotations || [];
  const recentSignups = recentSignupsData?.recentSignups || [];
  const stats = calculateQuotationStats(quotations);

  // 로딩 상태
  if (quotationsLoading || signupsLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container" style={{ textAlign: 'center', padding: '50px' }}>
          데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  // 에러 상태 확인
  if (quotationsError) {
    console.error('Quotations Error:', quotationsError);
  }
  if (signupsError) {
    console.error('Signups Error:', signupsError);
  }

  return (
    <>
      <div className="breadcrumb">
        <span>홈 &gt; 관리자 대시보드</span>
      </div>
      <div className="admin-dashboard">
        <div className="section">
          <div className="section-header">
            <h2 className="section-title">견적 문의 통계</h2>
            <button className="dashboard-button" onClick={() => navigate('/admin/quotes')}>견적 관리 이동</button>
          </div>
          
          {quotationsError ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
              견적 데이터를 불러오는 중 오류가 발생했습니다: {quotationsError.message}
            </div>
          ) : (
            <div className="stats-container">
              <div className="stat-card">
                <h3>전체 견적 현황</h3>
                <div className="stat-body">
                  <div>
                    <p>신규 문의</p>
                    <p className="count">{stats.newQuotations}</p>
                  </div>
                  <div>
                    <p>전체 문의</p>
                    <p className="count">{stats.totalQuotations}</p>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <h3>견적 상태 현황</h3>
                <div className="stat-body">
                  <div>
                    <p>답변 대기</p>
                    <p className="count">{stats.pendingQuotations}</p>
                  </div>
                  <div>
                    <p>답변 완료</p>
                    <p className="count">{stats.completedQuotations}</p>
                  </div>
                  <div>
                    <p>메일 전송 완료</p>
                    <p className="count">{stats.completedQuotations}</p>                  </div>
                </div>
              </div>
              <div className="stat-card chart-card">
                <h3>{new Date().getFullYear()}년 월별 견적 현황</h3>
                <Chart quotations={quotations} />
              </div>
            </div>
          )}
        </div>

        <div className="section">
          <div className="section-header">
            <h2 className="section-title">최근 회원가입</h2>
            <button className="dashboard-button" onClick={() => navigate('/admin/customer')}>회원 관리 이동</button>
          </div>
          <div className="recent-signups-container">
            {signupsError ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                회원 데이터를 불러오는 중 오류가 발생했습니다: {signupsError.message}
              </div>
            ) : (
              <table className="recent-signups-table">
                <thead>
                  <tr>
                    <th>아이디</th>
                    <th>회사명</th>
                    <th>담당자</th>
                    <th>연락처</th>
                    <th>이메일</th>
                    <th>가입일시</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignups.length > 0 ? (
                    recentSignups.map((user, index) => (
                      <tr key={user.id || index}>
                        <td data-label="아이디">{user.email?.split('@')[0] || ''}</td>
                        <td data-label="회사명">{user.company || ''}</td>
                        <td data-label="담당자">{user.name || ''}</td>
                        <td data-label="연락처">{formatPhone(user.phone || user.mobile)}</td>
                        <td data-label="이메일">{user.email || ''}</td>
                        <td data-label="가입일시">{formatDate(user.createdAt)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        최근 가입한 회원이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
             <div className="table-footer">...</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage; 